import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useThemeColors } from '../../hooks/useThemeColors';
import { getConfig } from '../../services/supabaseService';

function SpotifyPlayer() {
  const colors = useThemeColors();
  const [isEnabled, setIsEnabled] = useState(false);
  const [musicService, setMusicService] = useState('youtube'); // 'spotify', 'youtube', 'soundcloud'
  const [musicUrl, setMusicUrl] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const iframeRef = useRef(null);
  const embedUrlRef = useRef(''); // Ref para rastrear embedUrl sem causar re-renders

  // Extract YouTube Video ID from URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    url = url.trim();
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Extract SoundCloud ID from URL
  const extractSoundCloudId = (url) => {
    if (!url) return null;
    url = url.trim();
    
    // SoundCloud URLs são mais complexas, retornamos a URL completa
    if (url.includes('soundcloud.com')) {
      return url;
    }
    return null;
  };

  // Extract Spotify ID from URL (supports various formats)
  const extractSpotifyId = (url) => {
    if (!url) return null;
    url = url.trim();

    if (!url.includes('/') && !url.includes('?')) {
      return url;
    }

    const patterns = [
      /spotify\.com\/(?:intl-[a-z]{2}\/)?(?:track|album|playlist|artist|episode|show)\/([a-zA-Z0-9]+)/,
      /spotify\.com\/(?:track|album|playlist|artist|episode|show)\/([a-zA-Z0-9]+)/,
      /spotify:track:([a-zA-Z0-9]+)/,
      /spotify:album:([a-zA-Z0-9]+)/,
      /spotify:playlist:([a-zA-Z0-9]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Determine resource type from URL
  const getResourceType = (url) => {
    if (!url) return 'track';
    url = url.toLowerCase();
    if (url.includes('playlist')) return 'playlist';
    if (url.includes('album')) return 'album';
    if (url.includes('artist')) return 'artist';
    if (url.includes('episode')) return 'episode';
    if (url.includes('show')) return 'show';
    return 'track';
  };

  // Generate embed URL based on service
  const generateEmbedUrl = (url, service) => {
    if (!url) return null;

    switch (service) {
      case 'youtube':
        const ytId = extractYouTubeId(url);
        if (ytId) {
          // Check if it's a playlist
          if (url.includes('playlist')) {
            return `https://www.youtube.com/embed/videoseries?list=${ytId}&autoplay=0&controls=1&rel=0`;
          }
          return `https://www.youtube.com/embed/${ytId}?autoplay=0&controls=1&rel=0`;
        }
        return null;

      case 'soundcloud':
        const scUrl = extractSoundCloudId(url);
        if (scUrl) {
          return `https://w.soundcloud.com/player/?url=${encodeURIComponent(scUrl)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`;
        }
        return null;

      case 'spotify':
        const spotifyId = extractSpotifyId(url);
        const type = getResourceType(url);
        if (spotifyId) {
          return `https://open.spotify.com/embed/${type}/${spotifyId}?utm_source=generator`;
        }
        return null;

      default:
        return null;
    }
  };

  
  // Load music URL and service from database on mount
  useEffect(() => {
    const loadMusicConfig = async () => {
      try {
        // Load service preference
        const savedService = await getConfig('music_service') || localStorage.getItem('music_service') || 'youtube';
        setMusicService(savedService);

        // Load URL based on service
        const configKey = savedService === 'spotify' ? 'spotify_url' : 
                         savedService === 'soundcloud' ? 'soundcloud_url' : 'youtube_url';
        
        const savedUrl = await getConfig(configKey) || localStorage.getItem(configKey);
        
        if (savedUrl) {
          setMusicUrl(savedUrl);
          const newEmbedUrl = generateEmbedUrl(savedUrl, savedService);
          if (newEmbedUrl) {
            embedUrlRef.current = newEmbedUrl;
            setEmbedUrl(newEmbedUrl);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de música do banco:', error);
      }
    };
    loadMusicConfig();
  }, []);

  // Listen for changes to music URL in localStorage (when saved from Configurações)
  useEffect(() => {
    const handleStorageChange = (e) => {
      const configKeys = ['spotify_url', 'youtube_url', 'soundcloud_url', 'music_service'];
      if (configKeys.includes(e.key) && e.newValue) {
        if (e.key === 'music_service') {
          setMusicService(e.newValue);
        } else {
          const newUrl = e.newValue;
          if (newUrl !== musicUrl) {
            setMusicUrl(newUrl);
            setError('');
            const service = e.key.replace('_url', '').replace('spotify', 'spotify').replace('youtube', 'youtube').replace('soundcloud', 'soundcloud');
            const newEmbedUrl = generateEmbedUrl(newUrl, service);
            if (newEmbedUrl && newEmbedUrl !== embedUrlRef.current) {
              embedUrlRef.current = newEmbedUrl;
              setEmbedUrl(newEmbedUrl);
            }
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event (for same-tab updates)
    const handleCustomStorageChange = async () => {
      try {
        const savedService = await getConfig('music_service') || 'youtube';
        const configKey = savedService === 'spotify' ? 'spotify_url' : 
                         savedService === 'soundcloud' ? 'soundcloud_url' : 'youtube_url';
        const savedUrl = await getConfig(configKey);
        
        if (savedUrl && savedUrl !== musicUrl) {
          setMusicService(savedService);
          setMusicUrl(savedUrl);
          setError('');
          const newEmbedUrl = generateEmbedUrl(savedUrl, savedService);
          if (newEmbedUrl && newEmbedUrl !== embedUrlRef.current) {
            embedUrlRef.current = newEmbedUrl;
            setEmbedUrl(newEmbedUrl);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de música do banco:', error);
      }
    };

    window.addEventListener('musicUrlUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('musicUrlUpdated', handleCustomStorageChange);
    };
  }, [musicUrl, embedUrl]);


  const handleToggleEnabled = async (event) => {
    const enabled = event.target.checked;
    setIsEnabled(enabled);
    
    // When enabling, load the saved URL from database (mas não recarrega se já existe)
    if (enabled) {
      // Se já temos uma embedUrl válida, não recarregar (evita recriação do iframe)
      if (embedUrlRef.current) {
        return;
      }
      
      try {
        const savedService = await getConfig('music_service') || localStorage.getItem('music_service') || 'youtube';
        const configKey = savedService === 'spotify' ? 'spotify_url' : 
                         savedService === 'soundcloud' ? 'soundcloud_url' : 'youtube_url';
        
        const savedUrl = await getConfig(configKey) || localStorage.getItem(configKey);
        
        if (savedUrl) {
          setMusicService(savedService);
          setMusicUrl(savedUrl);
          setError('');
          const newEmbedUrl = generateEmbedUrl(savedUrl, savedService);
          if (newEmbedUrl) {
            embedUrlRef.current = newEmbedUrl;
            setEmbedUrl(newEmbedUrl);
          } else {
            setError(`URL do ${savedService === 'spotify' ? 'Spotify' : savedService === 'youtube' ? 'YouTube' : 'SoundCloud'} inválida. Configure uma URL válida em Configurações.`);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de música do banco:', error);
      }
    }
  };


  return (
    <Paper
      sx={{
        p: 1.5,
        background: colors.background.paper,
        border: `1px solid ${colors.border.primary}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MusicNoteIcon 
            sx={{ 
              color: musicService === 'spotify' ? '#1DB954' : 
                    musicService === 'youtube' ? '#FF0000' : '#FF5500',
              fontSize: 18 
            }} 
          />
          <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.75rem' }}>
            {musicService === 'spotify' ? 'Spotify' : 
             musicService === 'youtube' ? 'YouTube Music' : 'SoundCloud'}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={isEnabled}
              onChange={handleToggleEnabled}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1DB954',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1DB954',
                },
              }}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {isEnabled ? 'Ligado' : 'Desligado'}
            </Typography>
          }
        />
      </Box>

      {isEnabled && (
        <>
          {error && (
            <Alert severity="error" sx={{ mt: 0.5, mb: 0.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
              {error}
            </Alert>
          )}

          {embedUrl && (
            <Box
              sx={{
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${colors.border.primary}`,
                position: 'relative',
                backgroundColor: colors.background.paper,
              }}
            >
              {/* Iframe visível mas compacto - necessário para funcionar */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: musicService === 'youtube' ? '80px' : musicService === 'soundcloud' ? '166px' : '152px',
                  overflow: 'hidden',
                }}
              >
                <iframe
                  ref={iframeRef}
                  key={embedUrl}
                  src={embedUrl}
                  width="100%"
                  height={musicService === 'youtube' ? '80' : musicService === 'soundcloud' ? '166' : '152'}
                  frameBorder="0"
                  allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture; autoplay"
                  loading="lazy"
                  style={{
                    display: 'block',
                    border: 'none',
                  }}
                  title={`${musicService === 'spotify' ? 'Spotify' : musicService === 'youtube' ? 'YouTube' : 'SoundCloud'} Player`}
                />
              </Box>
            </Box>
          )}

          {!embedUrl && musicUrl && (
            <Alert severity="info" sx={{ mt: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
              Configure uma URL válida em Configurações
            </Alert>
          )}

          {!musicUrl && (
            <Box sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                Configure a URL de música em Configurações
              </Typography>
            </Box>
          )}
        </>
      )}

      {!isEnabled && (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            Ative para reproduzir música
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default SpotifyPlayer;
